interface AiErrorTemplate {
    title: string;
    message: string;
    fatalForBatch: boolean;
}

/**
 * Structural shape of the error objects this module classifies. Every field is
 * optional and read defensively (the code coerces via String()/Number() and
 * optional chaining). The index signature keeps it permissive so callers
 * passing richer Error subclasses don't need to narrow, and supports the
 * dynamic `err?.[key]` read in createWrappedAiError.
 */
export interface AiErrorInput {
    message?: unknown;
    error?: unknown;
    code?: unknown;
    category?: unknown;
    status?: unknown;
    statusCode?: unknown;
    upstreamStatusCode?: unknown;
    operatorAction?: unknown;
    retryable?: unknown;
    retryAfterSeconds?: unknown;
    aiError?: unknown;
    userMessage?: unknown;
    fatalAiProviderError?: unknown;
    [key: string]: unknown;
}

export interface AiErrorClassification {
    isAiProviderError: boolean;
    kind: string | null;
    title: string | null;
    message: string;
    userMessage: string;
    fatalForBatch: boolean;
    retryable: boolean;
    retryAfterSeconds?: number | null;
    operatorAction?: string;
}

/**
 * The codes the gateway's preflightPolicy (backend/src/services/ai-gateway.js)
 * refuses a call with before it reaches a provider, all sent as HTTP 429 with
 * `policyBlocked: true`: the per-call cost limit, the business's AI allow-list
 * (model or feature), and the daily and monthly AI spend limits. None is a
 * rate limit. The same request is refused again every time, so pausing,
 * resending, or moving it to another model never helps, and moving it would
 * change the operator's chosen model without asking.
 */
export const GATEWAY_POLICY_REFUSAL_CODES: ReadonlySet<string> = new Set([
    'per_call_too_expensive',
    'model_not_allowed',
    'feature_not_allowed',
    'daily_limit_reached',
    'hard_limit_reached',
]);

export function isGatewayPolicyRefusal(err: AiErrorInput | null | undefined): boolean {
    if (!err) return false;
    return err.policyBlocked === true || GATEWAY_POLICY_REFUSAL_CODES.has(String(err.code || ''));
}

/**
 * One sentence for an OpenRouter data-policy block: which model, why, and what
 * to do. It names no model family to switch to, because any family can be the
 * blocked one (2026-09-30: Claude Fable 5.1, an Anthropic model, is refused on
 * every pool key by the account's Zero Data Retention setting).
 */
export function dataPolicyBlockedMessage(modelName?: string | null): string {
    const subject = typeof modelName === 'string' && modelName.trim() ? modelName.trim() : 'The selected model';
    return `${subject} is blocked by this account's data policy, so pick another model, or ask the account owner to allow its providers in the OpenRouter privacy settings at openrouter.ai/settings/privacy.`;
}

/**
 * What the chosen model cannot do when OpenRouter answers 404 "No endpoints
 * found that ..." (2026-09-30, live: tools sent to tencent/hy-mt2-7b, a photo
 * sent to z-ai/glm-5.3). This is not the account's data policy: no privacy
 * setting fixes it. The gateway codes come from
 * backend/src/services/openrouter-no-endpoints.js.
 */
export type ModelCapabilityGap = 'tools' | 'images' | 'parameters' | 'endpoints';

const MODEL_CAPABILITY_CODES: Record<string, ModelCapabilityGap> = {
    model_no_tool_support: 'tools',
    model_no_image_support: 'images',
    model_no_parameter_support: 'parameters',
    model_no_endpoints: 'endpoints',
};

// An account-level block, in each wording OpenRouter uses. Checked before the
// capability test: the older data-policy wording also starts "No endpoints
// found".
const DATA_POLICY_MESSAGE_RE = /no allowed providers|matching your (data policy|guardrail)|zdr violation|data policy.*(setting|block)/i;
const NO_ENDPOINTS_MESSAGE_RE = /no endpoints (found|available)/i;

export function modelCapabilityGap(err: AiErrorInput | null | undefined): ModelCapabilityGap {
    const byCode = MODEL_CAPABILITY_CODES[String(err?.code || '').trim()];
    if (byCode) return byCode;
    const text = String(err?.message || err?.error || '');
    if (/tool use|tool compatibility/i.test(text)) return 'tools';
    if (/image input/i.test(text)) return 'images';
    if (/requested parameters/i.test(text)) return 'parameters';
    return 'endpoints';
}

/**
 * One sentence for a model-capability refusal: which model, what it cannot
 * do, and what to do. `task` wording is for a pipeline step (the model is set
 * per task on the AI Models page), `assistant` wording for the Assistant chat.
 */
export function modelCapabilityMessage(
    modelName: string | null | undefined,
    gap: ModelCapabilityGap,
    audience: 'task' | 'assistant' = 'task',
): string {
    const subject = typeof modelName === 'string' && modelName.trim() ? modelName.trim() : 'The selected model';
    const assistant = audience === 'assistant';
    if (gap === 'tools') {
        return assistant
            ? `${subject} cannot use the Assistant's tools, so pick another model.`
            : `${subject} cannot use tools, so pick another model for this task.`;
    }
    if (gap === 'images') {
        return assistant
            ? `${subject} cannot read images, so pick a model that can, or send the message without the image.`
            : `${subject} cannot read images, so pick a model that can for this task.`;
    }
    if (gap === 'parameters') {
        return assistant
            ? `${subject} does not support the settings the Assistant uses, so pick another model.`
            : `${subject} does not support the settings this request uses, so pick another model for this task.`;
    }
    return assistant
        ? `${subject} has no provider that can take this request, so pick another model.`
        : `${subject} has no provider that can take this request, so pick another model for this task.`;
}

const AI_ERROR_TEXT: Record<string, AiErrorTemplate> = {
    'ai-provider-config': {
        title: 'AI provider is not configured',
        message: 'Set OPENROUTER_API_KEYS in backend/.env and restart QuoteMaker.',
        fatalForBatch: true,
    },
    'ai-provider-auth': {
        title: 'AI provider authentication failed',
        message: 'Check OPENROUTER_API_KEYS in backend/.env. Replace revoked or mistyped keys, then restart QuoteMaker.',
        fatalForBatch: true,
    },
    'ai-provider-credit': {
        title: 'AI provider credit or spend limit reached',
        message: 'Check the OpenRouter account credits, spend limit, and model access for the configured backend key.',
        fatalForBatch: true,
    },
    'ai-provider-keys-quarantined': {
        title: 'AI provider keys are temporarily paused',
        message: 'QuoteMaker tried the configured key pool and every eligible key is cooling down. Retry after the shown cooldown; transient provider failures use a short cooldown, while auth or credit failures need operator action.',
        fatalForBatch: true,
    },
    'ai-provider-rate-limit': {
        title: 'AI provider is rate limiting requests',
        message: 'Wait briefly, reduce concurrent AI work, or add another backend key to OPENROUTER_API_KEYS.',
        fatalForBatch: true,
    },
    'ai-provider-model-busy': {
        title: 'The model\'s provider is busy',
        message: 'The provider behind this model is rate limiting OpenRouter\'s shared capacity. Retry shortly or choose another model for this task. Adding OpenRouter keys does not help.',
        fatalForBatch: false,
    },
    'ai-provider-unavailable': {
        title: 'AI provider is temporarily unavailable',
        message: 'Retry shortly. If it repeats, check the provider status and configured backend keys.',
        fatalForBatch: true,
    },
    'ai-request-too-large': {
        title: 'AI request is too large',
        message: 'Use a smaller file, shorter transcript, or fewer reference files for this AI step.',
        fatalForBatch: false,
    },
    'ai-model-config': {
        title: 'AI model is not configured',
        message: 'Choose a model in Settings before running this AI step.',
        fatalForBatch: true,
    },
    'ai-provider-data-policy': {
        title: 'No AI provider allowed for this model',
        message: dataPolicyBlockedMessage(),
        fatalForBatch: true,
    },
    'ai-model-capability': {
        title: 'This model cannot take this request',
        message: modelCapabilityMessage(null, 'endpoints'),
        fatalForBatch: true,
    },
};

export function classifyAiError(err: AiErrorInput | null | undefined): AiErrorClassification {
    const message = String(err?.message || err?.error || '').trim();
    const code = String(err?.code || '').trim();
    const category = String(err?.category || '').trim();
    const status = Number(err?.status || err?.statusCode || 0);
    const upstreamStatus = Number(err?.upstreamStatusCode || 0);

    let kind = category;
    if (!kind && code === 'no_matching_endpoints') kind = 'ai-provider-data-policy';
    if (!kind && MODEL_CAPABILITY_CODES[code]) kind = 'ai-model-capability';
    if (!kind && code === 'ai_provider_not_configured') kind = 'ai-provider-config';
    if (!kind && code === 'all_keys_quarantined') kind = 'ai-provider-keys-quarantined';
    if (!kind && code === 'upstream_provider_rate_limited') kind = 'ai-provider-model-busy';
    if (!kind && (code === 'upstream_401' || code === 'upstream_403' || upstreamStatus === 401 || upstreamStatus === 403)) kind = 'ai-provider-auth';
    if (!kind && (code === 'upstream_402' || upstreamStatus === 402)) kind = 'ai-provider-credit';
    if (!kind && (code === 'upstream_429' || upstreamStatus === 429)) kind = 'ai-provider-rate-limit';
    if (!kind && status === 413) kind = 'ai-request-too-large';
    if (!kind && DATA_POLICY_MESSAGE_RE.test(message)) kind = 'ai-provider-data-policy';
    if (!kind && NO_ENDPOINTS_MESSAGE_RE.test(message)) kind = 'ai-model-capability';
    if (!kind && /model not configured/i.test(message)) kind = 'ai-model-config';
    if (!kind && /OPENROUTER_API_KEY.*not configured|AI provider key is not configured/i.test(message)) kind = 'ai-provider-config';
    if (!kind && /API authentication error|provider authentication failed/i.test(message)) kind = 'ai-provider-auth';
    if (!kind && /credit|spend limit/i.test(message)) kind = 'ai-provider-credit';
    if (!kind && /all .*keys.*temporarily unavailable|keys.*quarantined/i.test(message)) kind = 'ai-provider-keys-quarantined';
    if (!kind && (upstreamStatus >= 500 || status === 502 || status === 503)) kind = 'ai-provider-unavailable';

    const template = AI_ERROR_TEXT[kind];
    if (!template) {
        return {
            isAiProviderError: false,
            kind: null,
            title: null,
            message,
            userMessage: message || 'AI request failed',
            fatalForBatch: false,
            retryable: Boolean(err?.retryable),
        };
    }

    const operatorAction = String(err?.operatorAction || '').trim();
    // A data-policy block is worded here, not taken from the backend, so an
    // older backend's "reassign this task to an Anthropic model" never shows.
    // A model-capability refusal is worded here too, from the gateway code or
    // the raw OpenRouter text, so it reads the same with or without the proxy.
    const modelId = typeof err?.model === 'string' ? err.model : null;
    const detail = kind === 'ai-provider-data-policy'
        ? dataPolicyBlockedMessage(modelId)
        : kind === 'ai-model-capability'
            ? modelCapabilityMessage(modelId, modelCapabilityGap(err))
            : (operatorAction || template.message);
    return {
        isAiProviderError: true,
        kind,
        title: template.title,
        message,
        userMessage: `${template.title}: ${detail}`,
        fatalForBatch: template.fatalForBatch,
        retryable: typeof err?.retryable === 'boolean' ? err.retryable : !template.fatalForBatch,
        retryAfterSeconds: Number(err?.retryAfterSeconds || 0) || null,
        operatorAction: detail,
    };
}

export function decorateAiError(err: AiErrorInput | null | undefined): AiErrorInput | null | undefined {
    const classification = classifyAiError(err);
    if (!classification.isAiProviderError || !err) return err;
    err.aiError = classification;
    err.userMessage = classification.userMessage;
    err.fatalAiProviderError = classification.fatalForBatch;
    err.retryable = classification.retryable;
    return err;
}

export function createWrappedAiError(prefix: string, err: AiErrorInput | null | undefined): Error {
    const classification = classifyAiError(err);
    // Cast to an index-assignable shape so the dynamic property copy below
    // type-checks; the cast erases at runtime (the value is a plain Error).
    const wrapped = new Error(classification.isAiProviderError
        ? `${prefix}: ${classification.userMessage}`
        : `${prefix}: ${err?.message || 'AI request failed'}`) as Error & Record<string, unknown>;
    for (const key of ['status', 'statusCode', 'code', 'category', 'upstreamStatusCode', 'operatorAction', 'retryable', 'retryAfterSeconds', 'qmSource', 'feature', 'model']) {
        // The `err!` is provably non-null here: the guard `err?.[key] !== undefined`
        // is only true when `err` is non-null. The `!` keeps runtime byte-identical
        // to the .js original while satisfying strict null checks.
        if (err?.[key] !== undefined) wrapped[key] = err![key];
    }
    if (classification.isAiProviderError) {
        wrapped.aiError = classification;
        wrapped.userMessage = classification.userMessage;
        wrapped.fatalAiProviderError = classification.fatalForBatch;
    }
    return wrapped;
}
